import { Pin02FilledIcon } from '@repo/studio-design-system-ui-icons/general/pin-02-filled'
import { Pin02Icon } from '@repo/studio-design-system-ui-icons/general/pin-02'
import * as React from 'react'
import { Button } from '@repo/studio-design-system-ui-button'
import type { ButtonSize, ButtonVariant, ButtonProps } from '@repo/studio-design-system-ui-button'
import {
  ListFavoriteToolsQueryRootKey,
  useFavoriteToolMutation,
  useUnfavoriteToolMutation,
} from '../../gen/service/creativefabrica/studiomediamatrix/v1'
import { useLoggedInAction } from '@repo/studio-hooks-auth/use-logged-in-action'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { ButtonTooltip } from '@repo/studio-design-system-ui-tooltip'
import type { AppSwitchBarItem } from '../top-app-bar/components/app-switch-bar'
import { useFavoriteToolTypes } from './hooks/use-favorite-tool-types'
import { useUpdateFavoritesInCache } from './hooks/use-update-favorites-in-cache'

export function FavoriteToolButton({
  className,
  variant = 'secondary',
  tool,
  size,
  icon,
}: {
  className?: string
  variant?: ButtonVariant
  tool: AppSwitchBarItem
  size?: ButtonSize
  icon?: React.ReactElement
}) {
  const queryClient = useQueryClient()
  const { addFavorite, removeFavorite } = useUpdateFavoritesInCache()
  const { favoriteToolTypes } = useFavoriteToolTypes()
  const toolType = tool.toolType
  const isFavorited = favoriteToolTypes.has(toolType)

  const favoriteMutation = useFavoriteToolMutation({
    onMutate: ({ tool: toolToAdd }) => {
      if (toolToAdd) {
        addFavorite(toolToAdd)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ListFavoriteToolsQueryRootKey] })
    },
  })

  const unfavoriteMutation = useUnfavoriteToolMutation({
    onMutate: ({ tool: toolToRemove }) => {
      if (toolToRemove) {
        removeFavorite(toolToRemove)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ListFavoriteToolsQueryRootKey] })
    },
  })

  // oxlint-disable-next-line require-await
  const { tryExecuteLoggedInAction: tryFavorite } = useLoggedInAction(async () => {
    if (isFavorited) {
      unfavoriteMutation.mutate({ tool: toolType })
    } else {
      favoriteMutation.mutate({ tool: toolType })
    }
  })

  const handleFavorite = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      tryFavorite()
    },
    [tryFavorite],
  )

  const isLoading = favoriteMutation.isPending || unfavoriteMutation.isPending

  const { t } = useTranslation('home')
  const tooltipValue = isFavorited
    ? t('studio.home.tools.unfavorite')
    : t('studio.home.tools.favorite')

  const renderIcon = () => {
    if (variant === 'ghost') {
      return (
        <div className="relative group/pin flex items-center justify-center">
          <Pin02FilledIcon className="absolute group-hover/pin:opacity-100 opacity-0 transition-opacity" />
          <Pin02Icon className="absolute group-hover/pin:opacity-0 opacity-100 transition-opacity" />
        </div>
      )
    }
    return <Pin02Icon />
  }

  return (
    <ButtonTooltip value={tooltipValue} side="bottom" offset={0} disabled={variant === 'ghost'}>
      <Button
        iconOnly
        className={className}
        size={size}
        variant={variant}
        onClick={handleFavorite}
        loading={isLoading}
        data-favorite
        aria-label={tooltipValue}
      >
        {icon ?? renderIcon()}
      </Button>
    </ButtonTooltip>
  )
}
